import { ReactNode } from "react";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import SpecialOrderPagination from "../SpecialOrder/Pagination";
import { gridHeadCellSx, gridHeadSx } from "../ExpressDocument/gridStyles";
import { MasterListState } from "./useMasterList";

export interface MasterColumn<Row> {
  key: string;
  label: string;
  align?: "left" | "right" | "center";
  render: (row: Row) => ReactNode;
}

interface MasterRowShape {
  uuid: string;
  code: string;
  is_active: boolean;
}

interface MasterTableProps<Row extends MasterRowShape> {
  columns: MasterColumn<Row>[];
  list: MasterListState<Row>;
  onEdit: (row: Row) => void;
}

const SKELETON_ROWS = 5;

export function ActiveChip({ isActive }: Readonly<{ isActive: boolean }>) {
  return (
    <Chip
      size="small"
      label={isActive ? "ใช้งาน" : "ปิดใช้งาน"}
      color={isActive ? "success" : "default"}
      variant={isActive ? "filled" : "outlined"}
    />
  );
}

function MasterRow<Row extends MasterRowShape>({
  row,
  columns,
  onEdit,
}: Readonly<{ row: Row; columns: MasterColumn<Row>[]; onEdit: (row: Row) => void }>) {
  const handleEdit = () => onEdit(row);

  return (
    <TableRow hover>
      {columns.map((column) => (
        <TableCell key={column.key} align={column.align}>
          {column.render(row)}
        </TableCell>
      ))}
      <TableCell align="center">
        <ActiveChip isActive={row.is_active} />
      </TableCell>
      <TableCell align="center">
        <Tooltip title="แก้ไข">
          <IconButton size="small" onClick={handleEdit} aria-label={`แก้ไข ${row.code}`}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

/** A page of a code list with its active state and an edit button per code. */
export default function MasterTable<Row extends MasterRowShape>({
  columns,
  list,
  onEdit,
}: Readonly<MasterTableProps<Row>>) {
  const { rows, totalCount, loading, page, setPage, limit, setLimit } = list;
  // The given columns plus สถานะ and the edit button.
  const columnCount = columns.length + 2;
  const isEmpty = !loading && rows.length === 0;
  const skeletonRows = loading ? Array.from({ length: SKELETON_ROWS }, (_, index) => index) : [];
  const skeletonCells = Array.from({ length: columnCount }, (_, index) => index);

  return (
    <Paper sx={{ p: 0, overflow: "hidden" }}>
      <TableContainer>
        <Table>
          <TableHead sx={gridHeadSx}>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} sx={gridHeadCellSx} align={column.align}>
                  {column.label}
                </TableCell>
              ))}
              <TableCell sx={gridHeadCellSx} align="center">
                สถานะ
              </TableCell>
              <TableCell width={64} />
            </TableRow>
          </TableHead>
          <TableBody>
            {skeletonRows.map((rowIndex) => (
              <TableRow key={`skeleton-${rowIndex}`}>
                {skeletonCells.map((cellIndex) => (
                  <TableCell key={cellIndex}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {isEmpty && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบรหัสตามเงื่อนไข
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((row) => <MasterRow key={row.uuid} row={row} columns={columns} onEdit={onEdit} />)}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack px={2} pt={1.5}>
        <Typography variant="body2" color="text.secondary">
          ทั้งหมด {totalCount} รายการ
        </Typography>
      </Stack>
      <SpecialOrderPagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />
    </Paper>
  );
}
